import useProfile from '~/apps/profile/use-profile';
import { useCallback, useState } from 'react';
import useSmartSendOnboardingProgress from './hooks/use-onboarding-progress';
import SmartSendSetup from './setup';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '../../lib/consts/RoutingConstants';
import useSmartSendVersionFeature from '~/apps/profile/use-smartsend-version-feature';
import { continueAccountLinking } from './components/continue-account-linking';
import { AchievementType } from '../../lib/context/ProfileContext';
import { SmartSendVersions } from './hooks/use-smartsend-version';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { EventSections } from '../../lib/events/EventLogger';
import useOnLoad from '~/routes/use-on-load';
import Loading from '~/components/loading/Loading';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

type props = {
  isXs: boolean;
  onPageLoad: () => void;
};

//http://localhost:3000/my-account/accounts?IntegrationSlug=ebay&IntegrationType=Marketplace&Success=True&Action=Link&AccountStatus=Active
const SmartSendSetupProcess = ({ isXs, onPageLoad }: props) => {
  const { track } = useSmartSendAnalytics();
  const { goTo } = useGoto();
  const [justLinked, setJustLinked] = useState<string | null>(null);
  const [justFailed, setJustFailed] = useState<string | null>(null);
  const { data, markAsDone, initialise, addOutstandingActions } = useProfile();
  const { setPreferredSmartSendVersion } = useSmartSendVersionFeature();
  const { setSmartSendOnboardingProgress } = useSmartSendOnboardingProgress();

  const markAsDoneProxy = useCallback(
    (
      keys: AchievementType[],
      outstandingActionsToAdd: string[],
      outstandingActionsToRemove: string[],
      meta: { [key: string]: any }
    ) => {
      // If they've just linked an account, set that state so we can show the correct screen
      if (keys.includes('SmartSendActionAccountLinked') && outstandingActionsToRemove.length === 1) {
        setJustLinked(outstandingActionsToRemove[0]);
      }

      return markAsDone(keys, outstandingActionsToAdd, outstandingActionsToRemove, meta);
    },
    [markAsDone]
  );

  const sendNow = useCallback(async () => {
    markAsDoneProxy(['SmartSendSetupCompleteScreen', 'SmartSendSignup'], [], [], {});
    setSmartSendOnboardingProgress('completed');
    await setPreferredSmartSendVersion(SmartSendVersions.SmarterSending);
    track({
      type: 'smartsend_setup_complete',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.Onboarding]),
        ...data.meta,
        achievements: data.achievements.filter((a) => a.value).map((a) => a.key)
      }
    });
    goTo(RoutingConstants.SmartSend);
  }, [
    goTo,
    setSmartSendOnboardingProgress,
    setPreferredSmartSendVersion,
    markAsDoneProxy,
    track,
    data.meta,
    data.achievements
  ]);

  // Load History for onboarding
  useOnLoad(() => {
    const load = async () => {
      console.log('Checking Smart Send Onboarding status');
      const res = await initialise();
      const completedAchievements = res?.achievements.filter((x) => x.value === true).map((x) => x.key) ?? [];
      if (
        !res.userSkipped &&
        !completedAchievements.includes('SmartSendActionFirstParcelSent') &&
        !completedAchievements.includes('SmartSendSetupCompleteScreen')
      ) {
        const integrationJustLinked = continueAccountLinking();
        if (integrationJustLinked?.integration) {
          // Linked account return flow
          if (integrationJustLinked.success) {
            markAsDoneProxy(
              ['SmartSendSetupAccountScreen', 'SmartSendActionAccountLinked'],
              [],
              [integrationJustLinked.integration],
              {}
            );
            setJustLinked(integrationJustLinked.integration);
          } else {
            markAsDoneProxy(['SmartSendSetupAccountScreen'], [integrationJustLinked.integration], [], {});
            setJustFailed(integrationJustLinked.integration);
          }
        } else {
          // Continuation of the onboarding process
          console.log('Starting or continuing Smart Send Onboarding');
          setSmartSendOnboardingProgress('started');
          onPageLoad();
        }
      } else {
        // Onboarding already completed or skipped
        console.log('Smart Send Onboarding already completed or skipped');
        setSmartSendOnboardingProgress('completed');
        await setPreferredSmartSendVersion(SmartSendVersions.SmarterSending);
        goTo(RoutingConstants.SmartSend);
      }
    };
    load();
  });

  if (!data) {
    return <Loading />;
  }

  return (
    <SmartSendSetup
      loading={data.loading}
      isXs={isXs}
      sendNow={sendNow}
      data={data}
      markAsDone={markAsDoneProxy}
      justLinked={justLinked}
      justFailed={justFailed}
      markAsTodo={addOutstandingActions}
    />
  );
};

export default SmartSendSetupProcess;
